import { ChipBadge } from '@dtx-company/design-system/src/components/ChipBadge/ChipBadge'
import { Controller, useFormContext } from 'react-hook-form'
import { Plan } from '@dtx-company/true-common/src/types/Plan'
import { PlanInterval } from '@dtx-company/true-common/src/types/PlanInterval'
import { PlanPurchaseCard } from './PlanPurchaseCard'
import { PlanPurchaseFormData } from '../../PlanPurchaseForm/PlanPurchaseFormData'
import { PlanPurchasePlanIntervalPickerProps } from './PlanPurchasePlanIntervalPickerProps'
import { PlanType } from '@dtx-company/true-common/src/types/planType'
import { Stack, Typography } from '@dtx-company/design-system/src'
import { deserializePlanIntervalQueryParam } from '@dtx-company/true-common/src/query-string-serialization/planInterval/deserializePlanIntervalQueryParam'
import { fireAnalyticsEvent } from '@dtx-company/inter-app/src/event-tracking/helpers/fireAnalyticsEvent'
import { getPriceText } from '@app/common/src/utils/getPriceText'
import { useEffect, useMemo } from 'react'
import { useInternalPlanPurchaseProviderContext } from '../PlanPurchaseProvider/context/useInternalPlanPurchaseProviderContext'
import { useInternalPlanPurchaseProviderContextUpdate } from '../PlanPurchaseProvider/context/useInternalPlanPurchaseProviderContextUpdate'
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined'

/**
 * Provides a plan interval picker for plan purchases that is hooked into the
 * PlanPurchaseForm for automatic 2-way binding and has built-in heap events.
 *
 * Option labels can be configured. Further customizations could be added as
 * needed in the future.
 *
 * Currently renders as a dropdown, could be expanded with options to render
 * other types of controls.
 */

const getPlanDetails = ({
  plans = [],
  planType,
  pricingGroup
}: {
  plans?: Plan[]
  planType: PlanType
  pricingGroup: string
}): {
  monthlyPlan: { pricePerMonth: string; pricePerYear: string }
  yearlyPlan: { pricePerMonth: string; pricePerYear: string; discount?: string }
} => {
  const monthly = plans.find(
    plan =>
      plan.type === planType &&
      plan.group === pricingGroup &&
      plan.interval === PlanInterval.MONTHLY
  )
  const yearly = plans.find(
    plan =>
      plan.type === planType && plan.group === pricingGroup && plan.interval === PlanInterval.YEARLY
  )

  return {
    monthlyPlan: {
      pricePerMonth: getPriceText({ priceInCents: monthly?.priceInCents }),
      pricePerYear: getPriceText({ priceInCents: (monthly?.priceInCents || 0) * 12 })
    },
    yearlyPlan: {
      pricePerMonth: getPriceText({ priceInCents: (yearly?.priceInCents || 0) / 12 }),
      pricePerYear: getPriceText({ priceInCents: yearly?.priceInCents }),
      discount: yearly?.discountPercent
        ? `${Math.floor(yearly?.discountPercent * 100)}%`
        : undefined
    }
  }
}

export function PlanPurchasePlanIntervalPicker(
  props: PlanPurchasePlanIntervalPickerProps
): JSX.Element {
  const { heapId } = props
  const { planInterval, planType, selectedPlan, pricingGroup, plans, ...rest } =
    useInternalPlanPurchaseProviderContext()
  const updateContext = useInternalPlanPurchaseProviderContextUpdate()
  const { control, setValue, watch } = useFormContext<PlanPurchaseFormData>()
  const value = watch('planInterval')

  const { monthlyPlan, yearlyPlan } = useMemo(
    () => getPlanDetails({ plans, planType, pricingGroup }),
    [plans, planType, pricingGroup]
  )

  // Sync form with context
  useEffect(() => {
    setValue('planInterval', planInterval)
  }, [planInterval, setValue])

  // Sync context with form
  useEffect(() => {
    updateContext({
      planInterval: deserializePlanIntervalQueryParam(value) || PlanInterval.YEARLY
    })
  }, [value, updateContext])

  const handleOnClick = (planType: PlanInterval): void => {
    updateContext({
      planInterval: planType
    })
    fireAnalyticsEvent(`${heapId}_Changed`, {
      value: planType,
      planType
    })
  }

  return (
    <Controller
      name="planInterval"
      control={control}
      render={() => {
        return (
          <Stack sx={{ width: '100%', mt: 2 }}>
            <Stack gap={3}>
              <Stack direction="row" gap={2} alignItems="center">
                <Typography variant="subtitleSmall" color="GrayText">
                  Billing cycle
                </Typography>
                {Boolean(yearlyPlan.discount) && (
                  <ChipBadge
                    icon={<BoltOutlinedIcon />}
                    color="success"
                    label={`${yearlyPlan.discount} discount when billed yearly`}
                    sx={{ textTransform: 'none' }}
                  />
                )}
              </Stack>

              <Stack direction="row" gap={2} sx={{ width: '100%' }}>
                <PlanPurchaseCard
                  title="Billed yearly"
                  mainPriceSection={
                    <>
                      <Typography color="green" variant="subtitleLarge">
                        {yearlyPlan.pricePerYear}
                      </Typography>
                      <Typography variant="subtitleSmall">/ year</Typography>
                    </>
                  }
                  secondaryPriceSection={
                    <Typography
                      color="GrayText"
                      variant="subtitleSmall"
                    >{`${yearlyPlan.pricePerMonth} / month`}</Typography>
                  }
                  onClick={() => handleOnClick(PlanInterval.YEARLY)}
                  isSelected={planInterval === PlanInterval.YEARLY}
                  isFeatured
                />

                <PlanPurchaseCard
                  title="Billed monthly"
                  mainPriceSection={
                    selectedPlan && (
                      <>
                        <Typography variant="subtitleLarge">{monthlyPlan.pricePerMonth}</Typography>
                        <Typography variant="subtitleSmall">/ month</Typography>
                      </>
                    )
                  }
                  secondaryPriceSection={
                    <Typography
                      color="GrayText"
                      variant="subtitleSmall"
                    >{`${monthlyPlan.pricePerYear} / year`}</Typography>
                  }
                  onClick={() => handleOnClick(PlanInterval.MONTHLY)}
                  isSelected={planInterval === PlanInterval.MONTHLY}
                />
              </Stack>
            </Stack>
          </Stack>
        )
      }}
    />
  )
}
